import type {
  BoxProps,
  ButtonProps,
  ThemingProps,
} from '@chakra-ui/react'
import {
  Box,
  Button,
  Flex,
  IconButton,
  Link,
  StylesProvider,
  Text,
  useDisclosure,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import versionData from '@/version.json'
import { deconstructVersion } from 'BrokerTsLib/src/Utils'
import { HelpDesk } from '../containers/HelpDesk'
import { useAuth } from '../context/AuthContext'
import { useApp } from '../hooks/useApp'
import { fetchOkKlientSpec } from '../hooks/useUser'
import { External, Help } from '../icons'
import type { IAvailableApps } from '../utils/availableApps'
import { Container } from './Container'

export interface FooterProps extends BoxProps, ThemingProps {
  hideLinks?: boolean
}

export interface FooterLinkProps extends ButtonProps {
  children: React.ReactNode
  href: string
  target?: HTMLAnchorElement['target']
}

export const FooterLink = ({
  children,
  href,
  target,
  ...rest
}: FooterLinkProps) => {
  return (
    <Button
      as={Link}
      variant="ghost"
      size="xs"
      colorScheme="whiteAlpha"
      href={href}
      target={target || '_blank'}
      rel="noopener noreferrer"
      _hover={{
        bg: 'transparent',
      }}
      {...rest}
    >
      {children}
    </Button>
  )
}

interface FooterConfig {
  /** Provozovatel e-mail (display + mailto). */
  email: string
  /** Copyright year in the footer text. */
  copyrightYear: number
  /** "Pojistné podmínky" link for OkKlient users. */
  pp: {
    link: string
    okKlientLink: string
  }
  /** "Fotodokumentace" link; omit to hide the link for this app. */
  fotodoku?: {
    link: string
    okKlientLink: string
  }
}

const carkulkaConfig: FooterConfig = {
  email: 'carkulka@okholding.cz',
  copyrightYear: 2023,
  pp: {
    link: 'https://intranet.okholding.cz/pp',
    okKlientLink: 'https://intranet.okklient.cz/pp-carkulka',
  },
  fotodoku: {
    link: 'https://intranet.okgroup.cz/okg-informace/13664',
    okKlientLink: 'https://intranet.okklient.cz/node/9535',
  },
}

const footerConfig: Record<IAvailableApps['appId'], FooterConfig> = {
  carkulka: carkulkaConfig,
  'carkulka-extra': carkulkaConfig,
  sme: carkulkaConfig,
  cestovko: {
    email: 'cestovko@okholding.cz',
    copyrightYear: 2023,
    pp: {
      link: 'https://intranet.okholding.cz/pp',
      okKlientLink: 'https://intranet.okklient.cz/pp-cestovka'
    }
  },
  chaloupka: {
    email: 'chaloupka@okholding.cz',
    copyrightYear: 2024,
    pp: {
      link: 'https://intranet.okholding.cz/pp',
      okKlientLink: 'https://intranet.okklient.cz/pp-chaloupka'
    }
  },
}

export const Footer = ({ sx, variant, hideLinks = false }: FooterProps) => {
  const { app } = useApp()
  const config = (app && footerConfig[app]) ?? carkulkaConfig

  const styles = useMultiStyleConfig('Footer', { variant })
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [isOkKlient, setIsOkKlient] = useState(false)

  const { user } = useAuth() ?? {}
  const ouId = user?.ouId

  useEffect(() => {
    fetchOkKlientSpec().then((r) => setIsOkKlient(r.data.belongsToOkKlient))
  }, [ouId])

  const ppLink = isOkKlient ? config.pp.okKlientLink : config.pp.link
  const fotodokuLink = config.fotodoku
    ? isOkKlient
      ? config.fotodoku.okKlientLink
      : config.fotodoku.link
    : undefined

  const version = deconstructVersion(versionData.version)

  return (
    <Box __css={styles.footer} as="footer" sx={sx}>
      <StylesProvider value={styles}>
        <Container>
          <Box as="section" __css={styles.content}>
            <IconButton
              aria-label="HelpDesk"
              colorScheme="primary"
              sx={{
                position: 'fixed',
                p: 4,
                right: 4,
                bottom: { base: 20, xl: 4 },
                boxShadow: '3xl',
                borderRadius: 'full',
                zIndex: 'helpDeskButton',
              }}
              onClick={isOpen ? onClose : onOpen}
            >
              <Help color="white" width={8} height={8} />
            </IconButton>

            <HelpDesk isOpen={isOpen} onOpen={onOpen} onClose={onClose} />

            <Box sx={{ color: 'white', fontSize: 'xs', mt: -2 }}>
              <Text>
                <span>Provozovatel systému:</span> OK GROUP a.s., Mánesova
                3014/16, 612 00 Brno,
                <a href={`mailto:${config.email}`}>{config.email}</a>
              </Text>
              <Text className="site-footer__text opacity-80">
                Copyright © OK GROUP a.s. {config.copyrightYear}. Designed by
                ctyri.media. Developed by Cebia, s.r.o.
              </Text>
              <Text
                fontSize={'2xs'}
                color={'whiteAlpha.600'}
                mt={2}
                mb={-4}
                title={`Verze ${version.year}.${version.month}.${version.patch}, Build ${version.build}+${version.commit}`}
              >
                Verze {version.year}.{version.month}.{version.patch}
              </Text>
            </Box>

            {!hideLinks && (
              <Flex sx={{ gap: 1 }}>
                {fotodokuLink && (
                  <FooterLink href={fotodokuLink}>
                    Fotodokumentace
                    <External />
                  </FooterLink>
                )}

                <FooterLink href={ppLink}>
                  Pojistné podmínky
                  <External />
                </FooterLink>

                <FooterLink href="https://atlas.okholding.cz/tabulky/3">
                  Produktová srovnání
                  <External />
                </FooterLink>
              </Flex>
            )}
          </Box>
        </Container>
      </StylesProvider>
    </Box>
  )
}
